/**
 * The `State` class extends the `EventEmitter` class and provides a reactive state management system.
 * It allows you to store and manage state in a type-safe manner, and emit events when the state changes.
 *
 * @template T - The type of the state object. Defaults to `IStateDefault`.
 *
 * @example
 * interface MyState {
 *   count: number;
 *   name: string;
 * }
 *
 * const initialState: Partial<MyState> = { count: 0, name: 'John' };
 * const state = new State<MyState>(initialState);
 *
 * # Get state value
 * console.log(state.get('count')); // 0
 *
 * # Set state value
 * state.setValues({ count: 1 });
 * console.log(state.get('count')); // 1
 *
 * # Use state in a React component
 * function MyComponent() {
 *   const appState = state.useState();
 *   return <div>{appState.count}</div>;
 * }
 */
import * as React from 'react';
import { EventEmitter } from 'utils/events';
import logger from './log';
import { isDebugMode } from './ui/debug';
import { isEqual } from './deepEqual';

type IStateDefault = Record<string, unknown>;

export class State<T extends object = IStateDefault> extends EventEmitter {
  protected $data: T;

  protected $initialData: T;

  get data() {
    return Object.freeze({ ...this.$data });
  }

  set data(value: T) {
    throw new Error('Cannot set data directly. Use the `set` method instead.');
  }

  protected $queue: Partial<T> = {};

  constructor(defaults: Partial<T> = {}) {
    super();
    this.$data = { ...defaults } as T;
    this.$initialData = { ...defaults } as T;
  }

  /**
   * @deprecated Use `setValues` instead
   */
  set(key: keyof T, value: T[keyof T], forceChange = false): T[keyof T] {
    if (isDebugMode()) {
      logger.warn(
        `State.set() is deprecated. Use setValues() instead to update multiple values at once. Key: ${String(key)}, Value: ${value}`
      );
    }
    const oldValue = this.$data[key];
    if (oldValue !== value || forceChange) {
      this.$data[key] = value;
      this.emit('change', [key], [value], [oldValue]);
    }
    return value;
  }

  /**
   * Get a key from the state object. If key is not provided, the entire state object is returned.
   */
  /**
   * Get a key from the state object. If key is not provided, the entire state object is returned.
   */
  get(): T;
  get<K extends keyof T>(key: K): T[K];
  get<K extends keyof T>(key?: K): T | T[K] {
    if (!key) {
      return this.$data;
    }
    return this.$data[key];
  }

  /**
   * Set multiple values in the state object
   * @param values - The values to set
   * @param options - Configuration options
   */
  /**
   * Updates multiple state values at once.
   *
   * @param values - Partial state object containing the values to update
   * @param options - Configuration options
   * @param options.reset - When true, resets state to initial values before applying new values (default: false)
   * @param options.emit - When true, emits a 'change' event after values are updated (default: true)
   *
   * @fires {Event} beforechange - Emitted before any values are changed
   * @fires {Event} change - Emitted after values have been changed (only if emit=true and values actually changed)
   *
   * @event beforechange
   * @type {Object}
   * @property {Array<keyof T>} changedKeys - Keys of values that will change
   * @property {Array<T[keyof T]>} changedValues - New values that will be set
   * @property {Array<T[keyof T]>} oldValues - Previous values being replaced
   * @property {Partial<T>} values - The complete values object passed to setValues
   * @property {T} previousState - A copy of the state before any changes
   *
   * @event change
   * @type {Object}
   * @property {Array<keyof T>} changedKeys - Keys that were changed
   * @property {Array<T[keyof T]>} changedValues - New values that were set
   * @property {Array<T[keyof T]>} oldValues - Previous values that were replaced
   * @property {Partial<T>} values - The complete values object passed to setValues
   * @property {T} currentState - The complete state after changes
   *
   * @returns {void}
   */
  setValues(values: Partial<T>, options?: { reset?: boolean; emit?: boolean }) {
    const { reset = false, emit = true } = options || {};

    if (reset) {
      this.reset();
    }

    const changedKeys: (keyof T)[] = [];
    const changedValues: T[keyof T][] = [];
    const oldValues: T[keyof T][] = [];

    // Compile the list of changed keys and values
    Object.keys(values).forEach((key) => {
      const value = values[key as keyof T];
      const oldValue = this.$data[key as keyof T];
      if (!isEqual(value, oldValue)) {
        changedKeys.push(key as keyof T);
        changedValues.push(value as T[keyof T]);
        oldValues.push(oldValue);
      }
    });

    if (!changedKeys.length) {
      // No changes detected, nothing to do
      return;
    }

    // Inform listeners about the changes that are going to happen
    // This is useful for components that need to prepare for the change or silently add other changes to the re-render
    // the final argument is the previous state and it has to be destructured to avoid passing a reference that will change in the next step
    this.emit('beforechange', changedKeys, changedValues, oldValues, values, { ...this.$data });

    // Apply changes
    Object.keys(values).forEach((key) => {
      const value = values[key as keyof T];
      const oldValue = this.$data[key as keyof T];
      if (!isEqual(value, oldValue)) {
        this.$data[key as keyof T] = value as T[keyof T];
      }
    });

    if (emit && changedKeys.length) {
      this.emit('change', changedKeys, changedValues, oldValues, values, this.$data);
    }

    return values;
  }

  // Queue the values to be set later
  queue(values: Partial<T>) {
    this.$queue = { ...this.$queue, ...values };
  }

  // Apply the queued values to the state
  applyQueue() {
    // Apply the queued values to the state
    this.setValues(this.$queue);
    // Clear the queue after applying
    this.$queue = {};
  }

  reset() {
    this.$data = { ...this.$initialData };
  }

  /**
   * Make it easier to use the hook without having to import it
   */
  useState() {
    // eslint-disable-next-line react-hooks/rules-of-hooks
    return useAppState<T>(this);
  }
}

export function useAppState<T extends object = IStateDefault>(stateInstance: State<T>): T {
  // we work with Symbols to maintain low memory footprint. We just need to trigger re-render
  const [, setState] = React.useState(Symbol('initial'));
  React.useEffect(() => {
    // `on` returns an unsubscribe closure — return it so the listener is removed on
    // unmount. Without this, every mount permanently leaks a 'change' listener on the
    // app-lifetime singletons (userSession(), tenantConfig(), …) and re-renders can fire
    // after unmount.
    const unsubscribe = stateInstance.on('change', (changedKeys: (keyof T)[]) => {
      setState(Symbol(String(changedKeys?.[0])));
    });
    return unsubscribe;
  }, [stateInstance]);

  return stateInstance.data;
}
